import { Box, Button, Checkbox, CircularProgress, Dialog, IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMeetingStore } from '../../store/meeting.store'
import { ACHARYA_EMAIL_PATTERN, getTransformedGroupDetails, Icons } from '../../utils/utilities'
import { useParams } from 'react-router-dom'
import { useUserStore } from '../../store/user.store'
import spreadSheetImg from '../../assets/spreadsheet.svg'

import TableHeader from '../common/TableHeader'
import api from '../../service/api'
import VisuallyHiddenInput from '../common/VisuallyHiddenInput'
import DataLoadingSpinner from '../common/DataLoadingSpinner'
import TryAgainMessageBlock from '../common/TryAgainMessageBlock'
import { Delete } from '@mui/icons-material'
import readXlsxFile from 'read-excel-file'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'


const userTableColumns = [
    { name: "Sl. No.", width: "80px", minWidth: "80px", align: "center", styles: { padding: "10px 5px" } },
    { name: "Host", width: "80px", minWidth: "80px", align: "center", styles: { padding: "10px 5px" } },
    { name: "Name", minWidth: "120px", align: "left", styles: { padding: "10px 5px" } },
    { name: "E-mail", minWidth: "120px", align: "left", styles: { padding: "10px 5px" } },
    { name: "", align: "right", styles: { padding: "10px 5px" } },
]

const MeetingAccessEditDialog = observer( ( { meeting, open, setOpen, handleClose } ) => {

    const [meetingAccessGroupsAndUsers, setMeetingAccessGroupsAndUsers] = useState( { users: [], groups: [], usersEmails: [] } )
    const [isLoadingAcYearsBatchesAndSectionsData, setIsLoadingAcYearsBatchesAndSectionsData] = useState( false )
    const [fetchingMeetingAccessDetails, setFetchingMeetingAccessDetails] = useState( false )
    const [accessDetailsFetchError, setAccessDetailsFetchError] = useState( null )
    const [academicYearsAnchorEl, setAcademicYearsAnchorEl] = useState( null )
    const [updatingGroupAccess, setUpdatingGroupAccess] = useState( false )
    const [groupSearchValue, setGroupSearchValue] = useState( '' )
    const [userSearchValue, setUserSearchValue] = useState( '' )
    const [togglingHost, setTogglingHost] = useState( false )

    const [addingNewUser, setAddingNewUser] = useState( false )
    const [removingUser, setRemovingUser] = useState( false )
    const [isDropping, setIsDropping] = useState( false )
    const [values, setValues] = useState( {
        selectedAcYear: '',
        selected_student_groups: [],
        added_users: [],
        added_users_emails: [],
    } )

    const nameInputRef = useRef()
    const emailInputRef = useRef()

    const { meeting_id } = useParams()
    const { palette, border } = useTheme()

    const UserStore = useUserStore()

    const groupsFilterFunc = ( item, groupType ) => {
        if ( groupType === 'batch' )
            return item.batch_assignment_id && +item.ac_year_id === +values.selectedAcYear && item.title?.toLowerCase()?.includes( groupSearchValue.toLowerCase() )
        if ( groupType === 'section' )
            return item.section_assignment_id && +item.ac_year_id === +values.selectedAcYear && item.title?.toLowerCase()?.includes( groupSearchValue.toLowerCase() )
    }

    const handleUserAddInputChange = ( event ) => {
        setUserSearchValue( event.target.value )
    }

    const handleUserlistFileDragLeave = ( event ) => {
        setIsDropping( false )
    }

    const handleUserlistFileDrop = ( event ) => {
        event.preventDefault()
        setIsDropping( false )
        handleOnExcelFileSelection( event )
    }

    const handleUserlistFileDragOver = ( event ) => {

        event.preventDefault()
        setIsDropping( true )
    }

    const setFieldValues = ( updated ) => {
        console.log( updated )

        setValues( prev => {
            const newValues = { ...prev }
            for ( const key in updated ) {
                if ( !Object.hasOwn( updated, key ) ) continue
                newValues[key] = updated[key]
            }
            return newValues
        } )
    }

    const handleOnExcelFileSelection = async ( event ) => {
        try {
            const file = event.type === "drop" ? event.dataTransfer.files[0] : event.target.files[0]
            if ( file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' )
                return toast( "Please select a Excel(.xlsx) file." )

            const excelData = await readXlsxFile( file )
            const newValue = structuredClone( meetingAccessGroupsAndUsers )
            const users = newValue.users
            const usersEmail = newValue.usersEmails

            const newUsers = []
            const newUsersEmails = []

            for ( const row of excelData ) {
                const [name, email] = row
                if ( !name || !email || !email?.match( ACHARYA_EMAIL_PATTERN ) )
                    continue
                if ( usersEmail?.includes( email ) )
                    continue

                const newUser = {}
                newUser.user_name = name
                newUser.user_email = email
                newUser.user_id = email
                newUsers.push( newUser )
                newUsersEmails.push( email )
            }
            if ( newUsers.length > 0 ) {
                const payload = {}
                payload.updateType = "add-users"
                payload.users = newUsers
                const { data: { data = [] } } = await api.onlineMeetings.updateMeetingAccess( meeting_id, payload )

                users.push( ...data )
                usersEmail.push( ...newUsersEmails )
            }

            setMeetingAccessGroupsAndUsers( newValue )
        } catch ( err ) {
            console.log( err )
        } finally {
            event.target.value = ''
        }
    }

    const toggleStudentGroupSelection = async ( group ) => {
        try {
            const groupExists = meetingAccessGroupsAndUsers.groups.findIndex( gp => String( gp.group_id ) === String( group.id ) )

            if ( groupExists >= 0 ) {
                setUpdatingGroupAccess( group.id )
                const payload = {}
                payload.updateType = "remove-group"
                payload.group = group
                await api.onlineMeetings.updateMeetingAccess( meeting_id, payload )
                setMeetingAccessGroupsAndUsers( prev => {
                    const newValue = structuredClone( prev )
                    newValue.groups.splice( groupExists, 1 )
                    return newValue
                } )
                setUpdatingGroupAccess( false )
            } else {
                setUpdatingGroupAccess( group.id )
                const payload = {}
                payload.updateType = "add-group"
                payload.group = group
                const { data: { data } } = await api.onlineMeetings.updateMeetingAccess( meeting_id, payload )
                console.log( data )

                setMeetingAccessGroupsAndUsers( prev => {
                    const newValue = structuredClone( prev )
                    newValue.groups.push( data )
                    return newValue
                } )
                setUpdatingGroupAccess( false )
            }
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to updated meeting access details." )
        }
    }

    const handleUserAddInputKeyDown = async ( event ) => {
        if ( event.key === 'Enter' ) {
            let value = event.target.value || ""
            value = value.split( "," ).map( em => em.trim() )
            let emails = value.filter( em => Boolean( em?.match( ACHARYA_EMAIL_PATTERN ) ) )
            emails = emails.filter( em => !meetingAccessGroupsAndUsers.usersEmails?.includes( em ) )
            const newValue = structuredClone( meetingAccessGroupsAndUsers )
            const users = newValue.users
            const usersEmail = newValue.usersEmails

            if ( emails.length > 0 ) {
                const { data: { data: usersDetails, all_fetched, missed_emails = [] } } = await api.users.fetchUserDetailsByEmail( { emails } )
                const usersPayload = []
                for ( const details of usersDetails ) {
                    const newUser = {}
                    newUser.user_name = details.employee_name || details.student_name || details.user_name
                    newUser.user_email = details.email
                    newUser.user_id = details.user_id
                    usersPayload.push( newUser )
                }
                const payload = {}
                payload.updateType = "add-users"
                payload.users = usersPayload
                const { data: { data = [] } } = await api.onlineMeetings.updateMeetingAccess( meeting_id, payload )
                users.push( ...data )
                usersEmail.push( ...data.map( u => u.user_email ) )
                if ( !all_fetched )
                    toast( "Couldn't find user details for some E-mails..." )
                setUserSearchValue( missed_emails.join( "," ) )
                setMeetingAccessGroupsAndUsers( newValue )
            } else {
                setUserSearchValue( '' )
            }
        }
    }

    const addNewUser = async () => {
        try {
            const name = nameInputRef.current?.value
            const email = emailInputRef.current?.value?.toLowerCase()
            if ( name && email ) {
                if ( meetingAccessGroupsAndUsers?.usersEmails?.includes( email ) )
                    return toast( "E-mail has already been added!" )
                setAddingNewUser( true )
                const newValue = structuredClone( meetingAccessGroupsAndUsers )
                const users = newValue.users
                const usersEmail = newValue.usersEmails
                const newUser = {}
                newUser.user_name = name
                newUser.user_email = email
                newUser.user_id = email

                const payload = {}
                payload.updateType = "add-users"
                payload.users = [newUser]
                const { data: { data } } = await api.onlineMeetings.updateMeetingAccess( meeting_id, payload )
                console.log( data )

                users.push( ...data )
                usersEmail.push( email )
                setMeetingAccessGroupsAndUsers( newValue )
                nameInputRef.current.value = ""
                emailInputRef.current.value = ""
            }
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to updated meeting access details." )
        } finally {
            setAddingNewUser( false )
        }
    }

    const removeUser = async ( user ) => {
        try {
            setRemovingUser( user.user_email )
            const newValue = structuredClone( meetingAccessGroupsAndUsers )
            const users = newValue.users
            const usersEmail = newValue.usersEmails

            const payload = {}
            payload.updateType = "remove-user"
            payload.user = user
            const userIndex = usersEmail.findIndex( u => u === user.user_email )
            await api.onlineMeetings.updateMeetingAccess( meeting_id, payload )
            if ( userIndex >= 0 ) {
                users.splice( userIndex, 1 )
                usersEmail.splice( userIndex, 1 )
            }
            setMeetingAccessGroupsAndUsers( newValue )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to updated meeting access details." )
        } finally {
            setRemovingUser( false )
        }
    }

    const toggleHost = async ( user ) => {

        try {
            const newValue = structuredClone( meetingAccessGroupsAndUsers )
            const newUsersList = [...newValue.users]
            const userIndex = newUsersList.findIndex( u => u.user_id === user.user_id )
            if ( userIndex >= 0 ) {
                setTogglingHost( user.user_id )
                const newUser = newUsersList[userIndex]
                const payload = {}
                payload.updateType = "toggle-host"
                payload.user = newUser
                await api.onlineMeetings.updateMeetingAccess( meeting_id, payload )
                newUser.host = !Boolean( newUser.host )
            }
            setMeetingAccessGroupsAndUsers( newValue )

        } catch ( err ) {
            console.log( err )
        } finally {
            setTogglingHost( false )
        }

    }

    const fetchMeetingAccessList = useCallback( async () => {
        try {
            setFetchingMeetingAccessDetails( true )
            const { data: { data } } = await api.onlineMeetings.fetchMeetingAccessGroupsAndUsers( meeting_id )
            const accessGroups = data.filter( accessDetails => accessDetails.entity_type === 'group' )
            const users = data.filter( accessDetails => accessDetails.entity_type === 'user' )
            const hostsIds = meeting.hosts?.map( h => h.user_id )
            users.forEach( user => {
                if ( hostsIds.includes( user.user_id ) )
                    user.host = true
                else
                    user.host = false
            } )
            const usersEmails = users.map( accessDetails => accessDetails.user_email )
            setMeetingAccessGroupsAndUsers( { users, groups: accessGroups, usersEmails } )
            if ( UserStore.getErrorsStatuses?.academicYearsAndBatches === true || UserStore.getErrorsStatuses?.academicYearsAndBatches === undefined ) {
                setIsLoadingAcYearsBatchesAndSectionsData( true )
                await UserStore.fetchUsersAllBatchesAndSectionList()
                setIsLoadingAcYearsBatchesAndSectionsData( false )
            }

            if ( !UserStore.getErrorsStatuses?.academicYearsAndBatches ) {
                setValues( { selectedAcYear: UserStore.getUserAcademicYears[0].ac_year_id } )
            }
            // }
            setAccessDetailsFetchError( null )
        } catch ( err ) {
            console.log( err )
            setAccessDetailsFetchError( { code: err?.response?.status || 500, message: err?.response?.data?.message || "Something went wrong! failed to fetch meeting access details." } )
        } finally {
            setFetchingMeetingAccessDetails( false )
        }
    }, [meeting_id, UserStore, meeting] )

    useEffect( () => {
        fetchMeetingAccessList()
    }, [fetchMeetingAccessList] )

    return (
        <Dialog open={open} slotProps={{
            paper: {
                sx: {
                    width: "90%",
                    maxWidth: "md"
                }
            }
        }} >
            <Box padding="20px">
                <Box display="flex" gap="20px" alignItems="center" justifyContent="space-between">
                    <Typography variant='h6'>Edit Meeting Access</Typography>
                    <IconButton onClick={handleClose ? handleClose : () => setOpen( false )}>
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>

                {!fetchingMeetingAccessDetails && !accessDetailsFetchError && <Box>

                    <Box marginTop="10px" overflow="clip" border={border[1]} borderRadius="20px">
                        <Box gap="10px" bgcolor={palette.contentBg} borderBottom={border[1]} display="flex">
                            <Button onClick={event => setAcademicYearsAnchorEl( event.currentTarget )} color="greyed" sx={{ borderRadius: "0", borderRight: border[1], cursor: "pointer", padding: "10px 5px 10px 10px", display: "flex", alignItems: "center", gap: "15px" }} >
                                <Typography fontSize="14px">
                                    {UserStore.getUserAcademicYears.find( ay => ay.ac_year_id === values.selectedAcYear )?.ac_year}
                                </Typography>
                                {Icons.default.DropDown}
                            </Button>
                            <Menu
                                anchorEl={academicYearsAnchorEl}
                                open={Boolean( academicYearsAnchorEl )}
                                onClose={() => setAcademicYearsAnchorEl( null )}
                            >
                                {UserStore.getUserAcademicYears.map( ( acYear ) => (
                                    <MenuItem sx={{ fontSize: "14px" }} onClick={() => setFieldValues( { selectedAcYear: acYear.ac_year_id } )} key={acYear.ac_year_id} selected={acYear.ac_year_id === values.selectedAcYear}>
                                        {acYear.ac_year}
                                    </MenuItem> )
                                )}
                            </Menu>
                            <TextField
                                id="group-search"
                                variant='standard'
                                sx={{ flexGrow: 1 }}
                                slotProps={{
                                    input: {
                                        disableUnderline: true,
                                        sx: { background: "none", height: "100%" }
                                    }
                                }}
                                placeholder='Search groups'
                                value={groupSearchValue}
                                onChange={e => setGroupSearchValue( e.target.value )}
                            />
                        </Box>
                        {!isLoadingAcYearsBatchesAndSectionsData && <Box maxHeight="150px" overflow="auto">
                            {UserStore.getUsersAllBatchesAndSectionList?.filter( item => groupsFilterFunc( item, 'batch' ) )?.map( ( batch, k ) => {
                                return ( <MenuItem onClick={() => toggleStudentGroupSelection( batch )} sx={{ display: "flex", gap: "5px", alignItems: "center", fontSize: "14px", padding: "0" }} dense key={batch.batch_assignment_id} value={batch.batch_assignment_id}>
                                    <Checkbox disabled={String( updatingGroupAccess ) === String( batch.id )} size='small' checked={Boolean( meetingAccessGroupsAndUsers.groups.find( b => String( b.group_id ) === String( batch.id ) ) )} />
                                    <Box>
                                        <Typography display="block" sx={{ color: String( updatingGroupAccess ) === String( batch.id ) ? "darkgray" : "inherit", fontStyle: updatingGroupAccess === String( batch.id ) ? "italic" : "inherit" }} >{batch.chip_label}</Typography>
                                        <Typography component="span" fontSize="12px" sx={{ color: String( updatingGroupAccess ) === String( batch.id ) ? "darkgray" : "inherit", fontStyle: updatingGroupAccess === String( batch.id ) ? "italic" : "inherit" }} >{batch.academic_details}</Typography>
                                    </Box>
                                    {String( updatingGroupAccess ) === String( batch.id ) && <CircularProgress size={14} />}
                                </MenuItem> )
                            } )}
                            {UserStore.getUsersAllBatchesAndSectionList?.filter( item => groupsFilterFunc( item, 'section' ) )?.map( ( section, k ) => {
                                return ( <MenuItem onClick={() => toggleStudentGroupSelection( section )} sx={{ display: "flex", gap: "5px", alignItems: "center", fontSize: "14px", padding: "0px" }} dense key={section.section_assignment_id} value={section.id}>
                                    <Checkbox disabled={String( updatingGroupAccess ) === String( section.id )} size='small' checked={Boolean( meetingAccessGroupsAndUsers.groups.find( s => s.group_id === section.id ) )} />
                                    <Box>
                                        <Typography display="block" sx={{ color: String( updatingGroupAccess ) === String( section.id ) ? "darkgray" : "inherit", fontStyle: String( updatingGroupAccess ) === String( section.id ) ? "italic" : "inherit" }}  >{section.chip_label}</Typography>
                                        <Typography component="span" fontSize="12px" sx={{ color: String( updatingGroupAccess ) === String( section.id ) ? "darkgray" : "inherit", fontStyle: String( updatingGroupAccess ) === String( section.id ) ? "italic" : "inherit" }}  >{section.academic_details}</Typography>
                                    </Box>
                                    {String( updatingGroupAccess ) === String( section.id ) && <CircularProgress size={14} />}
                                </MenuItem> )
                            } )}
                        </Box>}
                        {isLoadingAcYearsBatchesAndSectionsData && <DataLoadingSpinner waitingMessage="Loading batches and sections..." />}
                    </Box>
                    {meetingAccessGroupsAndUsers.groups.length > 0 && <Box marginTop="20px" display="flex" gap="10px" flexWrap="wrap" alignItems="center">
                        {meetingAccessGroupsAndUsers.groups.map( group => {

                            const groupDetails = getTransformedGroupDetails( group.group_details )

                            return (
                                <Box display="flex" gap="10px" alignItems="center" key={groupDetails.id} bgcolor={palette.contentBg} padding="5px 10px 5px 20px" borderRadius="40px" border={border[1]}>
                                    <Box>
                                        <Typography fontSize="14px" display="block" variant='subtitle2' >{groupDetails?.chip_label}</Typography>
                                        <Typography fontSize="12px" display="block" component="em">{groupDetails?.academic_details}</Typography>
                                    </Box>
                                    <IconButton onClick={() => toggleStudentGroupSelection( groupDetails )} size='small' color="error">
                                        {updatingGroupAccess === groupDetails?.id ? <CircularProgress size={20} /> : Icons.default.CloseIcon}
                                    </IconButton>
                                </Box>
                            )
                        } )}
                    </Box>}
                    <Box marginTop="20px">
                        <Typography variant='subtitle2'>Add or Edit Users' Access</Typography>
                        <Box border={border[1]} flexShrink={0} bgcolor={palette.contentBg} borderRadius="20px" display="flex" gap="10px">
                            <TextField
                                id='user-search'
                                sx={{ flexGrow: 1 }}
                                variant='standard'
                                slotProps={{
                                    input: {
                                        disableUnderline: true,
                                        sx: { background: "none", height: "40px", paddingLeft: "15px" }
                                    }
                                }}
                                value={userSearchValue}
                                onChange={handleUserAddInputChange}
                                onKeyDown={e => handleUserAddInputKeyDown( e )}
                                placeholder='Type comma seperated Acharya E-mail to fetch user details  ...' />
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" padding="5px">
                            <Typography color="GrayText" fontSize="12px">OR ADD MANUALLY</Typography>
                            <Typography color="GrayText" marginTop="-2px" component="em" display="block" fontSize="10px">Use this method, if data is not fetched from the E-mail</Typography>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap="10px" >
                            <Box flexGrow={1} border={border[1]} flexShrink={0} bgcolor={palette.contentBg} borderRadius="20px" display="flex" gap="10px">
                                <TextField id="user-name" inputRef={nameInputRef} sx={{ flexGrow: 1 }} variant='standard' slotProps={{ input: { disableUnderline: true, sx: { background: "none", height: "40px", paddingLeft: "15px" } } }} placeholder='Name' />
                            </Box>
                            <Box flexGrow={1} border={border[1]} flexShrink={0} bgcolor={palette.contentBg} borderRadius="20px" display="flex" gap="10px">
                                <TextField id='user-email' inputRef={emailInputRef} sx={{ flexGrow: 1 }} variant='standard' slotProps={{ input: { disableUnderline: true, sx: { background: "none", height: "40px", paddingLeft: "15px" } } }} placeholder='Acharya E-mail' />
                            </Box>
                            <Button disabled={addingNewUser} onClick={() => addNewUser( values )} variant='outlined' sx={{ borderRadius: "20px" }} startIcon={addingNewUser ? <CircularProgress size={20} /> : Icons.default.AddIcon}>
                                Add
                            </Button>
                        </Box>
                        <Box id="drop-container" position="relative" tabIndex={0} onDragOver={handleUserlistFileDragOver} onDragLeave={handleUserlistFileDragLeave} >
                            {/* DROPPING OVERLAY */}
                            {isDropping && <Box position="absolute" borderRadius="20px" display="flex" onDrop={e => handleUserlistFileDrop( e )} flexDirection="column" alignItems="center" border={border[1]} justifyContent="center" zIndex={10} sx={{ inset: 0, background: palette.grey[400] + "99", borderStyle: "dashed" }} >
                                <Typography sx={{ pointerEvents: "none" }} textAlign="center" fontSize="24px" className='animate-shake' fontWeight={800} ><em>Drop the users list here...</em></Typography>
                            </Box>}
                            {meetingAccessGroupsAndUsers.users.length > 0 && <Box flexGrow={1} marginTop="10px" overflow="clip" borderRadius="10px" border={border[1]} display="flex" flexDirection="column">
                                <TableContainer sx={{ height: "100%", maxHeight: "300px" }}>
                                    <Table stickyHeader>
                                        <TableHeader columns={userTableColumns} />
                                        <TableBody>
                                            {meetingAccessGroupsAndUsers.users?.map( ( user, index ) => {
                                                console.log( user )

                                                return (
                                                    <TableRow className='table-row' key={index}>
                                                        <TableCell align='center' sx={{ padding: "5px" }} >
                                                            {index + 1}
                                                        </TableCell>
                                                        <TableCell align='center' sx={{ padding: "5px" }} >
                                                            {togglingHost === user.user_id ? <CircularProgress size={20} /> : <Checkbox size='small' disabled={Boolean( togglingHost )} checked={Boolean( user.host )} onChange={() => toggleHost( user )} />}
                                                        </TableCell>
                                                        <TableCell sx={{ padding: "5px" }} >
                                                            <em>{user.user_name}</em>
                                                        </TableCell>
                                                        <TableCell sx={{ padding: "5px" }} >
                                                            <em>{user.user_email}</em>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ padding: "5px 20px" }} >
                                                            <IconButton onClick={() => removeUser( user )} color='error' size="small">
                                                                {removingUser === user.user_email ? <CircularProgress size={20} /> : <Delete sx={{ fontSize: "18px" }} />}
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            } )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>}
                            {meetingAccessGroupsAndUsers.users.length > 0 && <Box marginTop="10px" display='flex' justifyContent="flex-end">
                                <Button tabIndex={-1} component="label" variant='contained' color='secondary' disableElevation sx={{ borderRadius: "20px" }} >
                                    Import Users List From A File
                                    <VisuallyHiddenInput accept='.xlsx' type="file" onChange={( e ) => handleOnExcelFileSelection( e )} />
                                </Button>
                            </Box>}
                            {meetingAccessGroupsAndUsers.users.length === 0 && <Box border={border[1]} alignItems="center" justifyContent="center" flexGrow={1} gap="20px" padding="20px" display="flex" flexDirection="column" sx={{ borderStyle: "dashed", borderRadius: "20px", marginTop: "10px" }} >
                                <Box component="img" src={spreadSheetImg} alt="SpreadSheet" width="120px" />
                                <Typography textAlign="center" fontWeight={300} fontSize="14px"  >Type the users' <Typography fontSize="inherit" color={palette.secondary.main} component="em" fontWeight={600} size='small' >name</Typography> and <Typography fontSize="inherit" color={palette.secondary.main} component="em" fontWeight={600} size='small' >E-mails</Typography> and press <Typography fontSize="inherit" color={palette.secondary.main} component="em" fontWeight={600} size='small'>Add</Typography> button or choose a excel sheet with users name and email in the first two columns respectively in the first page.</Typography>
                                <Typography textAlign="center" fontWeight={500} fontSize="14px"  > Drag and Drop Excel file here or Browse </Typography>
                                <Button tabIndex={-1} component="label" variant='contained' color='secondary' disableElevation sx={{ borderRadius: "20px" }} >
                                    Select file
                                    <VisuallyHiddenInput accept='.xlsx' type="file" onChange={( e ) => handleOnExcelFileSelection( e )} />
                                </Button>
                            </Box>}
                        </Box>

                    </Box>

                    <Box display="flex" justifyContent="flex-end" marginTop="20px" >
                        <Button variant='contained' disableElevation onClick={handleClose ? handleClose : () => setOpen( false )}>
                            Okay
                        </Button>
                    </Box>

                </Box>}

                {fetchingMeetingAccessDetails && !accessDetailsFetchError && <Box display="flex" justifyContent="center" alignItems='center' padding="20px">
                    <DataLoadingSpinner waitingMessage="Fetching meeting's access details, please wait..." />
                </Box>}

                {accessDetailsFetchError && <Box display="flex" justifyContent="center" alignItems='center' padding="20px">
                    <TryAgainMessageBlock styles={{ flexGrow: 1 }} parent='component' loading={fetchingMeetingAccessDetails} code={accessDetailsFetchError?.code} getData={fetchMeetingAccessList} err={accessDetailsFetchError.message} />
                </Box>}

            </Box>
        </Dialog>
    )
} )

export default MeetingAccessEditDialog
